/**
 * Demo stub for @statsig/react-bindings.
 * Vite's resolve.alias in `vite.config.demo.ts` rewrites `@statsig/react-bindings` to this module,
 * so no real Statsig network requests are made from the demo bundle.
 *
 * Every gate evaluates to `true` by default — the demo wants the latest UI on, not the legacy paths.
 */
import type React from 'react';

declare global {
    interface Window {
        __DEMO_FEATURE_GATES__?: Record<string, boolean>;
    }
}

const noop = () => {};
const asyncNoop = async () => {};

// Gates the demo intentionally wants OFF, even though most others default to ON. Usually for
// experimental/internal-only flags that swap a polished surface for a WIP one.
const FORCED_OFF_GATES = new Set<string>([
    // Renders the legacy "Inbox" button instead of the polished "Message Center" + "Get Help"
    // chips in the header. Off → polished look.
    'project_inbox_poc',
    // Hides the "Earn $X" referral chip in the header (ReferralButton) — not relevant to the demo.
    'enable_impact_advocate_referrals',
    // Payroll Time Off renders Gusto's embedded SDK, which needs a live Gusto session and errors
    // without one. Off → the sidebar item and route disappear instead of showing an SDK error.
    'payroll_time_off',
    // Keeps the old payroll dashboard beside the new one. Off → a prospect sees one dashboard.
    'payroll_dashboard_v2_show_legacy',
]);

/**
 * Which payroll dashboard the demo shows. A prospect is not a member, so the member rollout gate
 * (`payroll_dashboard_v2`) can't decide it; this demo-only gate does, and the product gates below
 * follow it. On: the new dashboard alone, since `payroll_dashboard_v2_show_legacy` is forced off.
 * Off: the old dashboard alone.
 *
 * Flip the default for every visitor by changing it below and running the Deploy Demo workflow,
 * or for one visitor with `?demo_gates=demo_payroll_dashboard_v2:off` on the demo link.
 */
const DEMO_PAYROLL_DASHBOARD_V2 = 'demo_payroll_dashboard_v2';

const DEMO_GATE_DEFAULTS: Record<string, boolean> = {
    [DEMO_PAYROLL_DASHBOARD_V2]: true,
};

/**
 * Payroll gates the demo keeps off, so a prospect sees what members see in prod: the new
 * dashboard and nothing still behind a sub-gate (the Reasonable Comp chart, notifications, native
 * pay schedules...). Matched by prefix so a new payroll gate stays off here until it launches;
 * add a gate to `LAUNCHED_PAYROLL_GATES` once it is on for everyone in prod.
 */
const PAYROLL_GATE_PREFIXES = ['payroll_', 'reasonable_compensation_'];
const LAUNCHED_PAYROLL_GATES = new Set<string>([
    // The paid-of-goal progress bar on the Overview's Reasonable Comp card.
    'payroll_rc_overview_paid_of_goal',
]);

function isUnlaunchedPayrollGate(name: string): boolean {
    return PAYROLL_GATE_PREFIXES.some((prefix) => name.startsWith(prefix)) && !LAUNCHED_PAYROLL_GATES.has(name);
}

/** Product gates whose value is another gate's, so one demo switch moves them together. */
const FOLLOWING_GATES: Record<string, string> = {
    payroll_dashboard_v2: DEMO_PAYROLL_DASHBOARD_V2,
};

const URL_OVERRIDES_PARAM = 'demo_gates';
const URL_OVERRIDES_STORAGE_KEY = 'demo.gateOverrides';

/**
 * `?demo_gates=name:off,other:on`, kept for the tab's session so it survives in-app navigation
 * and reloads that drop the query string. Only the demo's own gates can be set this way: a link
 * must not be able to switch on a product gate the demo keeps off, such as `payroll_time_off`.
 */
function readUrlOverrides(): Record<string, boolean> {
    const fromUrl = new URLSearchParams(window.location.search).get(URL_OVERRIDES_PARAM);
    try {
        if (fromUrl === null) {
            return JSON.parse(sessionStorage.getItem(URL_OVERRIDES_STORAGE_KEY) ?? '{}');
        }
        const overrides = Object.fromEntries(
            fromUrl
                .split(',')
                .map((pair) => pair.split(':'))
                .filter(([name, value]) => name in DEMO_GATE_DEFAULTS && (value === 'on' || value === 'off'))
                .map(([name, value]) => [name, value === 'on'])
        );
        sessionStorage.setItem(URL_OVERRIDES_STORAGE_KEY, JSON.stringify(overrides));
        return overrides;
    } catch {
        return {};
    }
}

const URL_OVERRIDES = readUrlOverrides();

export function gateValueFor(name: string): boolean {
    // eslint-disable-next-line no-underscore-dangle
    const override = window.__DEMO_FEATURE_GATES__?.[name] ?? URL_OVERRIDES[name];
    if (override !== undefined) {
        return override;
    }
    if (name in FOLLOWING_GATES) {
        return gateValueFor(FOLLOWING_GATES[name]);
    }
    if (FORCED_OFF_GATES.has(name) || isUnlaunchedPayrollGate(name)) {
        return false;
    }
    return DEMO_GATE_DEFAULTS[name] ?? true;
}

const constantGet = (_key: string, fallback: unknown) => fallback;

// Dynamic-config values the demo wants populated. Keyed by config name; unlisted configs fall
// back to the caller's default (`get(key, fallback) → fallback`), matching prod's "config not set"
// behavior. Keys inside each config are snake_case because consumers read the raw config value
// (e.g. useHealthInsuranceConfig parses snake_case) or call `.get('snake_case_key', fallback)`.
const DEMO_DYNAMIC_CONFIGS: Record<string, Record<string, unknown>> = {
    // Surfaces the Health tab (Insurance → Health) and its acquisition page in the demo.
    health_insurance_config: {
        enabled: true,
        // Intentionally blank so the demo sandbox can't reach real third parties: an empty
        // calendly_url makes "Talk to a Solo agent" a no-op (a live Calendly booking widget
        // would otherwise load from calendly.com and let a prospect actually schedule), and an
        // empty quote_url makes "Get my quote" a no-op (it would otherwise open the real Solo
        // site in a new tab). The page still renders in full; only the external handoffs are off.
        calendly_url: '',
        quote_url: '',
        announcement_config: {
            text: 'Collective members receive $100 wellness credit annually.',
            suffix_link_text: 'Terms apply.',
            suffix_link_url: '',
        },
        // Off → skips the Solo token-handoff iframe (which would need a live handoff-token call).
        hbg_solo_config: { token_handoff_enabled: false },
    },
};

// Configs where every key reads the same value, for configs keyed by a list the stub shouldn't
// import (importing app modules here would cycle back through this alias).
const DEMO_UNIFORM_CONFIGS: Record<string, unknown> = {
    // Every payroll Overview widget in `write` mode, so the demo shows the actions (AutoPayroll
    // toggle, review and run) rather than the read-only default.
    payroll_widget_modes: 'write',
};

function dynamicConfigFor(name: string) {
    const value = DEMO_DYNAMIC_CONFIGS[name];
    const uniform = DEMO_UNIFORM_CONFIGS[name];
    return {
        value: value ?? {},
        get: (key: string, fallback: unknown) => value?.[key] ?? uniform ?? fallback,
    };
}

export const useStatsigClient = () => ({
    client: {
        checkGate: gateValueFor,
        getExperiment: () => ({ get: constantGet }),
        getDynamicConfig: dynamicConfigFor,
        updateUserAsync: asyncNoop,
        dataAdapter: { prefetchData: asyncNoop },
        logEvent: noop,
    },
});

export const useStatsigUser = () => ({
    updateUserSync: noop,
    updateUserAsync: asyncNoop,
    user: {},
});

export const useFeatureGate = (gateName: string) => ({ value: gateValueFor(gateName) });

export const useClientAsyncInit = () => ({
    client: { loadingStatus: 'Ready' as const, checkGate: gateValueFor },
    isLoading: false,
});

export function StatsigProvider({ children }: { children: React.ReactNode }) {
    return children as React.ReactElement;
}

export function useDynamicConfig(name: string) {
    return dynamicConfigFor(name);
}

export function useExperiment() {
    return { get: constantGet };
}

export type FeatureGateEvaluationOptions = Record<string, unknown>;
export type StatsigOptions = Record<string, unknown>;
export type StatsigUser = Record<string, unknown>;
